import {useCallback, useState} from 'react';
import {CreateGuestByParticipantPayload} from '@chekin/api';
import {FastAPIServiceError, Participant} from '@chekin/core';
import {FailedMultipleSaveResponse, OnboardingState} from '@chekin/shared';
import {useReservation} from 'context/reservation';
import {
  useMutateMultipleGuests,
  MutateMultipleGuestsSettledResult,
} from './useMutateMultipleGuests';
import {buildGuestInstantPayload, countDegradedGuestResults} from 'pages/Auth/utils';
import {useAnalytics} from 'analytics';
import {POSTHOG_EVENTS} from 'analytics/posthog';

type UseGuestCreationProps = {
  participants?: Participant[];
  onRegistered: (result: MutateMultipleGuestsSettledResult) => void;
};

type CreateGuestsArgs = {
  participants: Participant[];
  guestsData: OnboardingState['guestsData'];
  leaderGuestId?: string;
};

export type GuestErrors = Record<string, string[]>;

const parseGuestErrors = (
  errors: FailedMultipleSaveResponse<
    Omit<CreateGuestByParticipantPayload, 'reservation_id'>,
    FastAPIServiceError
  >[],
): GuestErrors =>
  errors.reduce<GuestErrors>((byGuest, {item, error}) => {
    const guestName = (item.full_name || '').trim() || 'Unknown guest';
    const messages = error.errors?.length
      ? error.errors.flatMap(fieldError => fieldError.message || [])
      : [error.message || (typeof error.detail === 'string' ? error.detail : '')].filter(
          Boolean,
        );
    if (messages.length) {
      byGuest[guestName] = [...(byGuest[guestName] ?? []), ...messages];
    }
    return byGuest;
  }, {});

export const useGuestCreation = ({participants, onRegistered}: UseGuestCreationProps) => {
  const {data: reservation} = useReservation();
  const {track} = useAnalytics();
  const [guestErrors, setGuestErrors] = useState<GuestErrors>({});

  const handleSuccess = useCallback(() => setGuestErrors({}), []);

  const handleError = useCallback(
    (
      error: FailedMultipleSaveResponse<
        Omit<CreateGuestByParticipantPayload, 'reservation_id'>,
        FastAPIServiceError
      >[],
    ) => setGuestErrors(parseGuestErrors(error)),
    [],
  );

  const handleSettled = useCallback(
    ({success, failed, threw}: MutateMultipleGuestsSettledResult) => {
      onRegistered({success, failed, threw});

      if (threw) {
        track(POSTHOG_EVENTS.INSTANT_CHECK_IN_FAILED, {
          failure_reason: 'guest_creation_error',
          reason: 'network_error',
          participants_count: participants?.length,
        });
        return;
      }

      const createdCount = success.length;
      const failedCount = failed.length;

      if (createdCount === 0) {
        if (failedCount > 0) {
          track(POSTHOG_EVENTS.INSTANT_CHECK_IN_FAILED, {
            failure_reason: 'guest_creation_error',
            failed_count: failedCount,
            participants_count: participants?.length,
            status_code: (failed[0]?.error as {status_code?: number})?.status_code,
          });
        }
        return;
      }

      const createdWithErrorsCount = countDegradedGuestResults(success);

      track(POSTHOG_EVENTS.INSTANT_CHECK_IN_COMPLETED, {
        guests_created_count: createdCount,
        created_with_errors_count: createdWithErrorsCount,
        is_partial: failedCount > 0,
      });

      if (createdWithErrorsCount > 0) {
        track(POSTHOG_EVENTS.INSTANT_CHECK_IN_CREATED_WITH_ERRORS, {
          created_with_errors_count: createdWithErrorsCount,
          created_count: createdCount,
          participants_count: participants?.length,
        });
      }

      if (failedCount > 0) {
        track(POSTHOG_EVENTS.INSTANT_CHECK_IN_PARTIAL, {
          created_count: createdCount,
          failed_count: failedCount,
          participants_count: participants?.length,
        });
      }
    },
    [track, participants, onRegistered],
  );

  const {mutateAsyncMultiple} = useMutateMultipleGuests({
    onSuccess: handleSuccess,
    onError: handleError,
    onSettled: handleSettled,
  });

  const createGuests = useCallback(
    ({participants: selected, guestsData, leaderGuestId}: CreateGuestsArgs) => {
      if (!reservation?.id || !selected.length) {
        onRegistered({success: [], failed: [], threw: true});
        return;
      }

      const payload = selected.map(participant =>
        buildGuestInstantPayload({
          guestsData,
          participant,
          reservationId: reservation.id,
          leaderGuestId,
        }),
      );

      setGuestErrors({});
      void mutateAsyncMultiple(payload);
    },
    [reservation?.id, mutateAsyncMultiple, onRegistered],
  );

  return {createGuests, guestErrors};
};
